import { updateDoc, doc, getDocs, collection, query, where, getDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { deleteUser, getAuth } from 'firebase/auth';
import { db } from '../config/firebase';
import { validatePromotion, getUserPermissions, ROLE_NAMES } from '../utils/permissions';

export const userService = {
  updateProfile: async (uid, profileData) => {
    try {
      await updateDoc(doc(db, 'users', uid), {
        ...profileData,
        updatedAt: new Date(),
      });
      return true;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  updateUserRole: async (uid, newRole, assignedUnit = null) => {
    try {
      const userData = await getDoc(doc(db, 'users', uid));
      const user = userData.data();

      await updateDoc(doc(db, 'users', uid), {
        currentRole: newRole,
        status: 'active',
        roleHistory: [
          ...(user.roleHistory || []),
          {
            role: newRole,
            startDate: new Date(),
            promotedBy: 'system',
          },
        ],
        ...(assignedUnit && { [assignedUnit.type]: assignedUnit.id }),
        updatedAt: new Date(),
      });

      return true;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  getUsersByRole: async (role) => {
    try {
      const q = query(
        collection(db, 'users'),
        where('currentRole', '==', role)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      throw new Error(error.message);
    }
  },

  searchUsers: async (searchTerm) => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const users = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter(user =>
          user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      return users;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  /**
   * Promote a user to a new role with hierarchy validation
   * @param {string} promoterRole - Role of the person doing the promotion
   * @param {string} targetUserId - UID of user being promoted
   * @param {string} newRole - Target role for promotion
   * @param {string} assignedUnit - Optional unit assignment (cell/chapter/comb/colony ID)
   * @returns {Promise<{success: boolean, message: string, data?: object}>}
   */
  promoteUser: async (promoterRole, targetUserId, newRole, assignedUnit = '') => {
    try {
      // Get target user data
      const targetUserDoc = await getDoc(doc(db, 'users', targetUserId));
      if (!targetUserDoc.exists()) {
        return {
          success: false,
          message: 'User not found'
        };
      }

      const targetUser = targetUserDoc.data();
      const currentRole = targetUser.currentRole;

      // Validate promotion
      const validation = validatePromotion(promoterRole, currentRole, newRole);
      if (!validation.valid) {
        return {
          success: false,
          message: validation.message
        };
      }

      // Update user role and permissions
      await updateDoc(doc(db, 'users', targetUserId), {
        currentRole: newRole,
        status: 'active',
        permissions: getUserPermissions(newRole),
        roleHistory: [
          ...(targetUser.roleHistory || []),
          {
            role: newRole,
            startDate: new Date(),
            promotedBy: promoterRole,
            promotionDate: new Date()
          }
        ],
        ...(assignedUnit && { assignedUnit }),
        updatedAt: new Date()
      });

      // Create promotion record
      await setDoc(doc(db, 'promotions', `${targetUserId}-${Date.now()}`), {
        userId: targetUserId,
        userEmail: targetUser.email,
        userName: targetUser.fullName,
        currentRole: currentRole,
        promotedRole: newRole,
        assignedUnit,
        promotedBy: promoterRole,
        promotionDate: new Date(),
        status: 'completed'
      });

      return {
        success: true,
        message: `${targetUser.fullName} promoted to ${ROLE_NAMES[newRole]} successfully!`,
        data: {
          userId: targetUserId,
          userName: targetUser.fullName,
          previousRole: currentRole,
          newRole: newRole
        }
      };
    } catch (error) {
      return {
        success: false,
        message: `Promotion failed: ${error.message}`
      };
    }
  },

  /**
   * MASTER ADMIN FUNCTIONS
   * These functions are restricted to master_admin role only
   */

  /**
   * Get all users in the system
   */
  getAllUsers: async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  /**
   * Get system-wide analytics
   */
  getSystemAnalytics: async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const users = snapshot.docs.map(doc => doc.data());

      // Calculate statistics
      const stats = {
        totalUsers: users.length,
        activeUsers: users.filter(u => u.status === 'active').length,
        inactiveUsers: users.filter(u => u.status !== 'active').length,
        usersByRole: {},
        usersByMountain: {},
        recentSignups: []
      };

      // Count by role
      users.forEach(user => {
        const role = user.currentRole || 'member';
        stats.usersByRole[role] = (stats.usersByRole[role] || 0) + 1;
      });

      // Count by mountain
      users.forEach(user => {
        const mountain = user.mountainOfInfluence || 'unassigned';
        stats.usersByMountain[mountain] = (stats.usersByMountain[mountain] || 0) + 1;
      });

      // Get recent signups (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      stats.recentSignups = users
        .filter(u => u.createdAt && new Date(u.createdAt.toDate?.() || u.createdAt) > sevenDaysAgo)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10);

      return stats;
    } catch (error) {
      console.error('Error fetching system analytics:', error);
      throw error;
    }
  },

  /**
   * Delete a user (master admin only)
   * Removes user from Firestore and Firebase Auth
   */
  deleteUser: async (userId) => {
    try {
      // Get user document to verify existence
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return {
          success: false,
          message: 'User not found'
        };
      }

      const userData = userDoc.data();

      // Delete from Firestore
      await deleteDoc(doc(db, 'users', userId));

      // Create deletion record
      await setDoc(doc(db, 'deletedUsers', userId), {
        userId,
        email: userData.email,
        fullName: userData.fullName,
        previousRole: userData.currentRole,
        deletedAt: Timestamp.now(),
        reason: 'Master admin deletion'
      });

      return {
        success: true,
        message: `User ${userData.fullName} has been deleted successfully`
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to delete user: ${error.message}`
      };
    }
  },

  /**
   * Suspend/unsuspend user
   */
  suspendUser: async (userId, suspend = true) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return {
          success: false,
          message: 'User not found'
        };
      }

      const userData = userDoc.data();

      await updateDoc(doc(db, 'users', userId), {
        status: suspend ? 'suspended' : 'active',
        updatedAt: Timestamp.now()
      });

      return {
        success: true,
        message: `User ${userData.fullName} has been ${suspend ? 'suspended' : 'unsuspended'}`
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to suspend user: ${error.message}`
      };
    }
  },

  /**
   * Get user details (master admin view)
   */
  getUserDetailsAdmin: async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return null;
      }

      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },

  /**
   * Update any user's role (master admin only - bypasses hierarchy)
   */
  updateUserRoleAdmin: async (userId, newRole) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return {
          success: false,
          message: 'User not found'
        };
      }

      const userData = userDoc.data();
      const oldRole = userData.currentRole;

      await updateDoc(doc(db, 'users', userId), {
        currentRole: newRole,
        permissions: getUserPermissions(newRole),
        roleHistory: [
          ...(userData.roleHistory || []),
          {
            role: newRole,
            startDate: new Date(),
            promotedBy: 'master_admin',
            promotionDate: new Date()
          }
        ],
        updatedAt: Timestamp.now()
      });

      // Create admin action record
      await setDoc(doc(db, 'adminActions', `${userId}-${Date.now()}`), {
        userId,
        userEmail: userData.email,
        userName: userData.fullName,
        action: 'role_update',
        previousRole: oldRole,
        newRole: newRole,
        performedBy: 'master_admin',
        timestamp: Timestamp.now()
      });

      return {
        success: true,
        message: `${userData.fullName} role updated to ${ROLE_NAMES[newRole]}`
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to update user role: ${error.message}`
      };
    }
  },

  /**
   * Get system audit log
   */
  getSystemAuditLog: async (limit = 50) => {
    try {
      const q = query(collection(db, 'adminActions'));
      const snapshot = await getDocs(q);
      const logs = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);

      return logs;
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  }
};

